import type { CourseModel } from "../types";

/**
 * Stufen-Wording aus dem Ausbildungsmodell.
 *
 * Das Backend (services/stundenplan.ts) rechnet identisch und liefert die fertige
 * Bezeichnung an der Klasse mit. Diese Kopie ist nur für Vorschauen in Formularen da,
 * wo die Auswahl noch nicht gespeichert ist.
 */

/** Nur die Modellfelder, die für die Beschriftung zählen. */
export type StageSource = Pick<CourseModel, "stageNoun" | "stageLabels">;

/** Bezeichnung einer Stufe: eigener Name aus dem Modell, sonst durchnummeriert. */
export function stageLabel(model: StageSource, stage: number): string {
  const custom = model.stageLabels?.[stage - 1]?.trim();
  if (custom) return custom;
  return `${stage}. ${model.stageNoun}`;
}

/**
 * Laufende Stufe zum Stichtag. Ab dem Kursbeginn Stufe 1, danach je Takt hochgezählt.
 * Ohne oder vor dem Kursbeginn null. Nicht auf die Stufenanzahl begrenzt, damit ein
 * abgelaufener Kurs erkennbar bleibt.
 */
export function currentStage(
  courseStartDate: string | null | undefined,
  rhythmMonths: number,
  ref: Date = new Date()
): number | null {
  if (!courseStartDate) return null;
  const [y, m, d] = String(courseStartDate).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  const start = new Date(y, m - 1, d);
  const perStage = rhythmMonths > 0 ? rhythmMonths : 12;

  // In ganzen Monaten rechnen, sonst verschieben unterschiedlich lange Monate
  // und die Sommerzeit den Stufenwechsel um einen Tag.
  let months = (ref.getFullYear() - start.getFullYear()) * 12 + (ref.getMonth() - start.getMonth());
  if (ref.getDate() < start.getDate()) months--;
  if (months < 0) return null;

  return Math.floor(months / perStage) + 1;
}

/** Was an der Klasse steht: Stufe, "Abgeschlossen" oder nichts. */
export function describeStage(
  courseStartDate: string | null | undefined,
  model: CourseModel | null | undefined,
  ref: Date = new Date()
): string | null {
  if (!model) return null;
  const stage = currentStage(courseStartDate, model.rhythmMonths, ref);
  if (stage === null) return null;
  return stage > model.stageCount ? "Abgeschlossen" : stageLabel(model, stage);
}

/** Gaengige Takte fuer die Auswahl im Modell-Formular. */
export const RHYTHM_OPTIONS: { months: number; label: string }[] = [
  { months: 12, label: "Jährlich (12 Monate)" },
  { months: 6, label: "Halbjährlich (6 Monate)" },
  { months: 4, label: "Alle 4 Monate" },
  { months: 3, label: "Vierteljährlich (3 Monate)" },
];
